import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FiHome, FiSearch, FiCompass, FiFilm,
  FiMessageCircle, FiBell, FiPlusSquare,
  FiUser, FiLogOut, FiX, FiMenu
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const NAV_ITEMS = [
  { path: '/',              icon: FiHome,          label: 'Home'          },
  { path: '/search',        icon: FiSearch,        label: 'Search'        },
  { path: '/explore',       icon: FiCompass,       label: 'Explore'       },
  { path: '/reels',         icon: FiFilm,          label: 'Reels'         },
  { path: '/messages',      icon: FiMessageCircle, label: 'Messages'      },
  { path: '/notifications', icon: FiBell,          label: 'Notifications' },
];

export default function Sidebar({ onCreatePost }) {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out!');
    navigate('/login');
  };

  const avatarFallback = (name, size = 36) => (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg,#E91E8C,#7B2FF7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.38, flexShrink: 0
    }}>
      {name?.[0]?.toUpperCase()}
    </div>
  );

  const NavLinks = ({ onNavClick }) => (
    <>
      {NAV_ITEMS.map(({ path, icon: Icon, label }) => (
        <NavLink
          key={path}
          to={path}
          className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
          onClick={onNavClick}
          title={label}
        >
          <Icon />
          <span className="sidebar-label">{label}</span>
        </NavLink>
      ))}

      <button
        className="sidebar-link"
        onClick={() => { onCreatePost?.(); onNavClick?.(); }}
        title="Create"
      >
        <FiPlusSquare />
        <span className="sidebar-label">Create</span>
      </button>

      <NavLink
        to={`/profile/${user?.id}`}
        className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
        onClick={onNavClick}
        title="Profile"
      >
        {user?.avatar
          ? <img src={user.avatar} alt="" className="avatar"
              style={{ width: 22, height: 22, flexShrink: 0 }} />
          : <FiUser />
        }
        <span className="sidebar-label">Profile</span>
      </NavLink>
    </>
  );

  return (
    <>
      {/* ── Desktop / Tablet Sidebar ── */}
      <aside className="sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/')}>
          <img
            src="https://noris.in/wp-content/uploads/2023/01/logo.png"
            alt="Noris"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <span className="gradient-text sidebar-label"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700 }}>
            MSocial
          </span>
        </div>

        <nav className="sidebar-nav">
          <NavLinks />
        </nav>

        <div className="sidebar-user">
          <NavLink to={`/profile/${user?.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, textDecoration: 'none' }}>
            {user?.avatar
              ? <img src={user.avatar} alt="" className="avatar" style={{ width: 34, height: 34 }} />
              : avatarFallback(user?.full_name, 34)
            }
            <div style={{ minWidth: 0, flex: 1 }} className="sidebar-label sidebar-user-text">
              <p className="truncate font-bold" style={{ fontSize: 'var(--fs-sm)' }}>{user?.full_name}</p>
              <p className="truncate text-3" style={{ fontSize: 'var(--fs-xs)' }}>@{user?.username}</p>
            </div>
          </NavLink>
          <button className="btn btn-icon btn-ghost sidebar-label" onClick={handleLogout} title="Logout">
            <FiLogOut size={17} />
          </button>
        </div>
      </aside>

      {/* ── Mobile Top Bar ── */}
      <div className="mobile-topbar">
        <span className="gradient-text"
          style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700 }}>
          MSocial
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button className="btn btn-icon" onClick={() => navigate('/notifications')}>
            <FiBell size={22} />
          </button>
          <button className="btn btn-icon" onClick={() => navigate('/messages')}>
            <FiMessageCircle size={22} />
          </button>
          <button className="btn btn-icon" onClick={() => setDrawerOpen(p => !p)}>
            {drawerOpen ? <FiX size={22} /> : <FiMenu size={22} />}
          </button>
        </div>
      </div>

      {/* ── Mobile Drawer ── */}
      {drawerOpen && (
        <div className="mobile-drawer" onClick={() => setDrawerOpen(false)}>
          <div className="mobile-drawer-inner" onClick={e => e.stopPropagation()}>
            <div className="sidebar-logo" style={{ marginBottom: 8 }} onClick={() => { navigate('/'); setDrawerOpen(false); }}>
              <img
                src="https://noris.in/wp-content/uploads/2023/01/logo.png"
                alt="Noris" style={{ height: 28 }}
                onError={e => { e.target.style.display = 'none'; }}
              />
              <span className="gradient-text"
                style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1.15rem', marginLeft: 8 }}>
                MSocial
              </span>
            </div>
            <nav className="sidebar-nav">
              <NavLinks onNavClick={() => setDrawerOpen(false)} />
            </nav>
            <div className="sidebar-user" style={{ marginTop: 'auto' }}>
              <NavLink to={`/profile/${user?.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, textDecoration: 'none' }} onClick={() => setDrawerOpen(false)}>
                {user?.avatar
                  ? <img src={user.avatar} alt="" className="avatar" style={{ width: 36, height: 36 }} />
                  : avatarFallback(user?.full_name, 36)
                }
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p className="truncate font-bold" style={{ fontSize: 'var(--fs-sm)' }}>{user?.full_name}</p>
                  <p className="truncate text-3" style={{ fontSize: 'var(--fs-xs)' }}>@{user?.username}</p>
                </div>
              </NavLink>
              <button className="btn btn-icon btn-ghost" onClick={handleLogout} title="Logout">
                <FiLogOut size={17} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile Bottom Nav ── */}
      <nav className="mobile-bottom-nav">
        <NavLink to="/"                    className={({ isActive }) => `mob-nav-item${isActive ? ' active' : ''}`}><FiHome /></NavLink>
        <NavLink to="/search"              className={({ isActive }) => `mob-nav-item${isActive ? ' active' : ''}`}><FiSearch /></NavLink>
        <button  className="mob-nav-item"  onClick={onCreatePost}><FiPlusSquare style={{ width: 28, height: 28 }} /></button>
        <NavLink to="/explore"             className={({ isActive }) => `mob-nav-item${isActive ? ' active' : ''}`}><FiCompass /></NavLink>
        <NavLink to={`/profile/${user?.id}`} className={({ isActive }) => `mob-nav-item${isActive ? ' active' : ''}`}>
          {user?.avatar
            ? <img src={user.avatar} className="avatar" style={{ width: 26, height: 26 }} alt="" />
            : <FiUser />
          }
        </NavLink>
      </nav>
    </>
  );
}
